/**
 * Tank Rendering — neon wireframe body + stroked turret.
 * Body rotates with movement roll; turret rotates independently with aim angle.
 * Active player at full alpha, inactive dimmed.
 * Sakurai flashing: hit flash (bright+dark), damage urgency pulse, state alpha.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { TANK_VISUAL, Z, VISUAL, PHYSICS, TRAJECTORY, AIM_FX, AIM, MOMENTUM, TUMBLE, HOP, DUELIST_SLASH, WORLD } from '../constants';
import type { Player } from '../types';
import type { ParticleSystem } from './particles';
import { simulateTrajectory } from './trajectory';
import { getDefaultWeapon, lookupWeapon } from '../weapons';
import { chargeToPower } from '../game/aiming';
import { drawTankShape, strokeTankShape, getTankShapeDef, lighten } from './shapes';
import { lookupTank } from '../tanks';
import { getSkinForTank, BORDER_STYLES, TITLES } from '../skins/storage';
import { drawBarrel, getNubAnimSpeed } from './nub-tips';
import { isPointInTerrain } from '../terrain/grid';

// Stagger counter for energy cape layers (Sakurai: "stagger timing")
let auraFrame = 0;

// Trajectory cache — skip simulation + Graphics rebuild when quantized inputs match
let trajCache: {
  points: { x: number; y: number }[];
  angle: number; power: number;
  x: number; y: number; wind: number;
  playerIndex: number; color: number;
  weaponId: string;
} | null = null;

export interface TankVisual {
  outerContainer: Container;
  bodyGfx: Graphics;
  turretGfx: Container;       // container for barrel + animated tip (rotation/alpha here)
  barrelGfx: Graphics;        // barrel drawing surface (child of turretGfx)
  aimLine: Graphics;
  trajectoryGfx: Graphics;
  // Sakurai: character flash state
  flashOverlay: Graphics;
  flashTimer: number;
  flashColor: number;
  flashDarkColor: number;
  // Animated barrel tip child (Vortex spiral, Gyro propeller)
  tipGfx?: Graphics;
  // Aim sweep trail — trailing angle that lags behind turret (Sakurai blade trail)
  sweepTrailGfx: Graphics;
  aimSparkCooldown: number;
  trailAngle: number;  // trailing edge — chases turret with delay, creating wake
  // Ragdoll spin sparks cooldown
  spinSparkCooldown: number;
  // Cached per-tank visual properties
  tankRadius: number;        // bodyRadius from TankVisualDef
  drawnTankId: string;       // last tankId drawn — triggers redraw on change
  // Resolved skin overrides
  skinBodyId: string;        // tankId used for body shape (skin override or default)
  skinNubId: string;         // tankId used for nub (skin override or default)
  skinColor: number;         // resolved color (skin override or default)
  // Border style overlay
  borderGfx: Graphics;       // animated border overlay (drawn each frame)
  skinBorderId: string;      // current border style ID ('' = none)
  // Title text above tank
  titleText: Text;           // PixiJS Text child on outerContainer
  skinTitle: string;         // current title ID ('' = none)
  skinTitleColor: number;    // resolved title color
  skinTitleRainbow: boolean; // rainbow cycling hue override
}

// drawTankBarrel delegates to shared nub-tips module
function drawTankBarrel(gfx: Graphics, nubId: string, color: number): Graphics | undefined {
  return drawBarrel(gfx, nubId, color);
}

// ─── Border style rendering ─────────────────────────────────────────────────

/** HSL hue (0-360) to hex color */
function hueToHex(h: number, s = 1, l = 0.55): number {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return (Math.round(f(0) * 255) << 16) | (Math.round(f(8) * 255) << 8) | Math.round(f(4) * 255);
}

function drawBorderOverlay(gfx: Graphics, borderId: string, bodyId: string, radius: number, elapsed: number): void {
  gfx.clear();
  if (!borderId || borderId === 'none') return;

  const t = elapsed;

  switch (borderId) {
    case 'rainbow': {
      // Per-edge rainbow — each edge of the shape gets a different cycling hue
      const def = getTankShapeDef(bodyId);
      if (def.type === 'polygon' && def.vertices) {
        const verts = def.vertices(radius);
        const vertCount = verts.length / 2;
        const hueOffset = t * 120; // cycles through full spectrum

        // Pass 1: outer glow (wider, dimmer)
        for (let i = 0; i < vertCount; i++) {
          const x1 = verts[i * 2], y1 = verts[i * 2 + 1];
          const ni = (i + 1) % vertCount;
          const x2 = verts[ni * 2], y2 = verts[ni * 2 + 1];
          const hue = ((i / vertCount) * 360 + hueOffset) % 360;
          gfx.moveTo(x1, y1); gfx.lineTo(x2, y2);
          gfx.stroke({ color: hueToHex(hue), width: 5, alpha: 0.2 });
        }
        // Pass 2: bright core
        for (let i = 0; i < vertCount; i++) {
          const x1 = verts[i * 2], y1 = verts[i * 2 + 1];
          const ni = (i + 1) % vertCount;
          const x2 = verts[ni * 2], y2 = verts[ni * 2 + 1];
          const hue = ((i / vertCount) * 360 + hueOffset) % 360;
          const pulse = 0.65 + Math.sin(t * 3 + i * 0.7) * 0.2;
          gfx.moveTo(x1, y1); gfx.lineTo(x2, y2);
          gfx.stroke({ color: hueToHex(hue, 1, 0.6), width: 2.5, alpha: pulse });
        }
        // Pass 3: white highlight (thinnest, brightest)
        for (let i = 0; i < vertCount; i++) {
          const x1 = verts[i * 2], y1 = verts[i * 2 + 1];
          const ni = (i + 1) % vertCount;
          const x2 = verts[ni * 2], y2 = verts[ni * 2 + 1];
          const hue = ((i / vertCount) * 360 + hueOffset) % 360;
          gfx.moveTo(x1, y1); gfx.lineTo(x2, y2);
          gfx.stroke({ color: hueToHex(hue, 0.3, 0.85), width: 1, alpha: 0.5 });
        }
      } else {
        // Fallback for draw-based shapes (vortex) — layered full strokes
        for (let layer = 0; layer < 3; layer++) {
          const hue = (t * 120 + layer * 120) % 360;
          strokeTankShape(gfx, bodyId, radius + layer * 0.5, hueToHex(hue), 2.5, 0.4);
        }
      }
      break;
    }

    case 'gold_shimmer': {
      // Gold with traveling shimmer highlight
      const shimmerPos = (t * 2) % (Math.PI * 2);
      strokeTankShape(gfx, bodyId, radius, 0xDDAA33, 2, 0.5);
      // Bright flash traveling around perimeter
      const flashAngle = shimmerPos;
      const fx = Math.cos(flashAngle) * radius;
      const fy = Math.sin(flashAngle) * radius;
      gfx.circle(fx, fy, 3);
      gfx.fill({ color: 0xFFEE88, alpha: 0.6 + Math.sin(t * 8) * 0.3 });
      // Second shimmer point
      const fx2 = Math.cos(flashAngle + Math.PI) * radius;
      const fy2 = Math.sin(flashAngle + Math.PI) * radius;
      gfx.circle(fx2, fy2, 2);
      gfx.fill({ color: 0xFFDD66, alpha: 0.4 });
      break;
    }

    case 'silver_shimmer': {
      const shimmerPos = (t * 1.8) % (Math.PI * 2);
      strokeTankShape(gfx, bodyId, radius, 0xAABBCC, 2, 0.5);
      const fx = Math.cos(shimmerPos) * radius;
      const fy = Math.sin(shimmerPos) * radius;
      gfx.circle(fx, fy, 3);
      gfx.fill({ color: 0xDDEEFF, alpha: 0.5 + Math.sin(t * 6) * 0.3 });
      break;
    }

    case 'fire': {
      // Flickering orange-red border
      const flicker = 0.5 + Math.sin(t * 12) * 0.15 + Math.sin(t * 7.3) * 0.15 + Math.sin(t * 19) * 0.1;
      strokeTankShape(gfx, bodyId, radius, 0xFF4400, 2.5, flicker * 0.6);
      strokeTankShape(gfx, bodyId, radius + 1, 0xFF8800, 1.5, flicker * 0.3);
      // Fire tips at random angles
      for (let s = 0; s < 4; s++) {
        const a = (Math.PI / 2) * s + Math.sin(t * 3 + s * 1.7) * 0.3;
        const tipLen = 3 + Math.sin(t * 8 + s * 2.1) * 2;
        gfx.moveTo(Math.cos(a) * radius, Math.sin(a) * radius);
        gfx.lineTo(Math.cos(a) * (radius + tipLen), Math.sin(a) * (radius + tipLen));
        gfx.stroke({ color: 0xFFAA22, width: 1.5, alpha: flicker * 0.7 });
      }
      break;
    }

    case 'ice': {
      // Cool blue with crystal sparkles
      strokeTankShape(gfx, bodyId, radius, 0x66CCFF, 2, 0.45);
      // Sparkle dots
      for (let s = 0; s < 6; s++) {
        const a = (Math.PI / 3) * s + t * 0.5;
        const sparkle = Math.sin(t * 5 + s * 1.3);
        if (sparkle > 0.3) {
          const sx = Math.cos(a) * (radius + 1);
          const sy = Math.sin(a) * (radius + 1);
          gfx.circle(sx, sy, 1.5);
          gfx.fill({ color: 0xDDEEFF, alpha: sparkle * 0.6 });
        }
      }
      break;
    }

    case 'toxic': {
      // Pulsing toxic green with drip effect
      const pulse = 0.4 + Math.sin(t * 4) * 0.2;
      strokeTankShape(gfx, bodyId, radius, 0x44FF22, 2, pulse);
      strokeTankShape(gfx, bodyId, radius + 2, 0x22AA11, 1.5, pulse * 0.4);
      break;
    }

    case 'void': {
      // Dark purple with pulsing void energy
      const pulse = 0.3 + Math.sin(t * 2.5) * 0.15;
      strokeTankShape(gfx, bodyId, radius, 0x6622AA, 2.5, pulse + 0.2);
      strokeTankShape(gfx, bodyId, radius + 3, 0x330066, 2, pulse);
      // Inner dark ring
      strokeTankShape(gfx, bodyId, radius - 1, 0x110022, 1.5, pulse + 0.1);
      break;
    }

    case 'blood': {
      // Dark red with dripping pulse
      const pulse = 0.5 + Math.sin(t * 3) * 0.2;
      strokeTankShape(gfx, bodyId, radius, 0xCC1133, 2.5, pulse);
      // Drip particles at bottom
      const dripY = radius + 2 + Math.sin(t * 2) * 1.5;
      gfx.circle(0, dripY, 1.5);
      gfx.fill({ color: 0xCC1133, alpha: pulse * 0.5 });
      break;
    }

    case 'electric': {
      // Cyan with crackling lightning arcs
      strokeTankShape(gfx, bodyId, radius, 0x22EEFF, 1.5, 0.5);
      // Random lightning arcs (seeded by elapsed time)
      for (let s = 0; s < 3; s++) {
        const seed = Math.floor(t * 8 + s * 100);
        const pseudo = Math.sin(seed * 12345.6789) * 0.5 + 0.5;
        if (pseudo > 0.4) {
          const a = pseudo * Math.PI * 2;
          const x1 = Math.cos(a) * radius;
          const y1 = Math.sin(a) * radius;
          const x2 = x1 + (Math.sin(seed * 1.3) * 6);
          const y2 = y1 + (Math.cos(seed * 2.1) * 6);
          gfx.moveTo(x1, y1); gfx.lineTo(x2, y2);
          gfx.stroke({ color: 0xAAFFFF, width: 1.2, alpha: 0.7 });
        }
      }
      break;
    }

    case 'sakura': {
      // Pink with floating petal shapes
      strokeTankShape(gfx, bodyId, radius, 0xFF88CC, 1.5, 0.4);
      for (let s = 0; s < 5; s++) {
        const a = (Math.PI * 2 / 5) * s + t * 0.3;
        const dist = radius + 1 + Math.sin(t * 1.5 + s) * 2;
        const px = Math.cos(a) * dist;
        const py = Math.sin(a) * dist;
        // Tiny petal (two arcs)
        gfx.ellipse(px, py, 2, 1);
        gfx.stroke({ color: 0xFFAADD, width: 0.8, alpha: 0.4 + Math.sin(t * 2 + s) * 0.2 });
      }
      break;
    }

    case 'sunset': {
      // Layered orange-to-red — 3 warm hue strokes shifting over time
      for (let layer = 0; layer < 3; layer++) {
        const blend = (Math.sin(t * 1.5 + layer * 1.2) + 1) / 2;
        const r = 0xFF;
        const g = Math.round(0x44 + blend * 0x44);
        const b = Math.round(blend * 0x22);
        const c = (r << 16) | (g << 8) | b;
        strokeTankShape(gfx, bodyId, radius + layer * 0.3, c, 2, 0.35 + blend * 0.2);
      }
      break;
    }
  }
}

// Duelist lightsaber constants
const SABER_LENGTH = 70;
const SABER_HILT_LENGTH = 11;

function drawWireframeSaber(gfx: Graphics): void {
  // Hilt — stroked rect (gray)
  gfx.rect(0, -2.5, SABER_HILT_LENGTH, 5);
  gfx.stroke({ color: 0x888888, width: 1.5 });

  // Guard crossbar at hilt junction — short perpendicular line
  gfx.moveTo(SABER_HILT_LENGTH, -6);
  gfx.lineTo(SABER_HILT_LENGTH, 6);
  gfx.stroke({ color: 0x999999, width: 2.0 });

  // Blade — 3-pass red glow line (thinner rapier)
  const bladePasses = [
    { width: 4,   color: 0xFF2244, alpha: 0.3 },
    { width: 2,   color: 0xFF6482, alpha: 0.6 },
    { width: 0.8, color: 0xFFCCDD, alpha: 1.0 },
  ];
  for (const pass of bladePasses) {
    gfx.moveTo(SABER_HILT_LENGTH, 0);
    gfx.lineTo(SABER_HILT_LENGTH + SABER_LENGTH, 0);
    gfx.stroke({ color: pass.color, width: pass.width, alpha: pass.alpha });
  }

  // Tip — small stroked circle (pink)
  gfx.circle(SABER_HILT_LENGTH + SABER_LENGTH, 0, 2);
  gfx.stroke({ color: 0xFF8899, width: 1.2 });
}

export function createTankVisuals(
  worldContainer: Container,
  players: Player[],
): TankVisual[] {
  return players.map((player) => {
    // Resolve skin overrides
    const skin = getSkinForTank(player.tankId);
    const skinBodyId = skin.bodyShape ?? player.tankId;
    const skinNubId = skin.nubType ?? player.tankId;
    const skinColor = skin.color ?? player.color;

    // Body — per-tank wireframe shape from shape registry (skin-aware)
    const tankDef = lookupTank(player.tankId);
    const bodyShapeDef = lookupTank(skinBodyId);
    const tankRadius = bodyShapeDef.visual.bodyRadius;

    const outerContainer = new Container();
    outerContainer.zIndex = Z.TANKS;
    outerContainer.position.set(player.x, player.y - (tankRadius - PHYSICS.ECB_RADIUS));
    worldContainer.addChild(outerContainer);

    // Sweep trail — renders behind body (Sakurai display priority: characters above effects)
    const sweepTrailGfx = new Graphics();
    outerContainer.addChild(sweepTrailGfx);
    const bodyGfx = new Graphics();
    drawTankShape(bodyGfx, skinBodyId, tankRadius, skinColor);
    bodyGfx.alpha = 0.75; // inactive by default
    outerContainer.addChild(bodyGfx);

    // Turret / Sword — Duelist nub gets a wireframe saber, everyone else gets wireframe turret
    const isDuelist = skinNubId === 'duelist';
    const turretGfx = new Container();
    const barrelGfx = new Graphics();
    turretGfx.addChild(barrelGfx);
    let tipGfx: Graphics | undefined;
    if (isDuelist) {
      drawWireframeSaber(barrelGfx);
    } else {
      tipGfx = drawTankBarrel(barrelGfx, skinNubId, skinColor);
      if (tipGfx) turretGfx.addChild(tipGfx);
    }
    outerContainer.addChild(turretGfx);

    // Border style overlay (animated, drawn each frame)
    const borderGfx = new Graphics();
    outerContainer.addChild(borderGfx);
    const skinBorderId = skin.borderStyle ?? '';

    // Title text above tank
    const skinTitle = skin.title ?? '';
    const skinTitleRainbow = skin.titleRainbow ?? false;
    const skinTitleColor = skin.titleColor ?? skinColor;
    const titleEntry = TITLES.find(t => t.id === skinTitle);
    const titleText = new Text({
      text: (titleEntry && skinTitle !== 'none') ? titleEntry.label : '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 12,
        fontWeight: 'bold',
        fill: skinTitleRainbow ? 0xffffff : skinTitleColor,
        stroke: { color: 0x000000, width: 3 },
        dropShadow: {
          color: skinTitleRainbow ? 0xffffff : skinTitleColor,
          blur: 6,
          alpha: 0.5,
          distance: 0,
        },
      }),
    });
    titleText.anchor.set(0.5, 1);
    titleText.position.set(0, -(tankRadius + 12));
    titleText.visible = skinTitle !== '' && skinTitle !== 'none';
    outerContainer.addChild(titleText);

    // Flash overlay — wireframe white strokes, sits on top, initially invisible
    // Sakurai: "mix bright and dark colors in the flashing effect"
    const flashOverlay = new Graphics();
    strokeTankShape(flashOverlay, skinBodyId, tankRadius, 0xffffff, 3, 1.0);
    if (isDuelist) {
      // Saber flash overlay — stroked line
      flashOverlay.moveTo(SABER_HILT_LENGTH, 0);
      flashOverlay.lineTo(SABER_HILT_LENGTH + SABER_LENGTH, 0);
      flashOverlay.stroke({ color: 0xffffff, width: 3 });
    } else {
      // Turret flash overlay — stroked line + circle
      flashOverlay.moveTo(0, 0);
      flashOverlay.lineTo(TANK_VISUAL.TURRET_LENGTH, 0);
      flashOverlay.stroke({ color: 0xffffff, width: 3 });
      flashOverlay.circle(TANK_VISUAL.TURRET_LENGTH, 0, TANK_VISUAL.TIP_RADIUS);
      flashOverlay.stroke({ color: 0xffffff, width: 3 });
    }
    flashOverlay.visible = false;
    outerContainer.addChild(flashOverlay);

    // Aim extension line (world-space, separate from outerContainer)
    const aimLine = new Graphics();
    aimLine.zIndex = Z.TANKS + 1;
    worldContainer.addChild(aimLine);

    // Trajectory preview dots (world-space)
    const trajectoryGfx = new Graphics();
    trajectoryGfx.zIndex = Z.TANKS + 1;
    worldContainer.addChild(trajectoryGfx);

    return {
      outerContainer, bodyGfx, turretGfx, barrelGfx, aimLine, trajectoryGfx,
      flashOverlay, flashTimer: 0, flashColor: 0xffffff, flashDarkColor: 0x222222,
      tipGfx,
      sweepTrailGfx, aimSparkCooldown: 0,
      trailAngle: -player.angle, // screen-space angle (negated)
      spinSparkCooldown: 0,
      tankRadius,
      drawnTankId: player.tankId,
      skinBodyId,
      skinNubId,
      skinColor,
      borderGfx,
      skinBorderId,
      titleText,
      skinTitle,
      skinTitleColor,
      skinTitleRainbow,
    };
  });
}

/** Trigger a character flash — Sakurai bright+dark alternating */
export function triggerTankFlash(
  visuals: TankVisual[],
  playerIndex: number,
  brightColor: number,
  darkColor: number,
  duration: number = 0.35,
): void {
  const v = visuals[playerIndex];
  if (!v) return;
  v.flashTimer = duration;
  v.flashColor = brightColor;
  v.flashDarkColor = darkColor;
}

export function updateTankVisuals(
  players: Player[],
  visuals: TankVisual[],
  currentPlayer: number,
  showAim: boolean,
  dt: number,
  wind: number = 0,
  particles?: ParticleSystem,
  tutorialOpponentFade: number = 1,
): void {
  for (let i = 0; i < players.length; i++) {
    const p = players[i];
    const v = visuals[i];
    if (!v) continue;

    // Hide dead tanks
    v.outerContainer.visible = p.alive;
    v.aimLine.clear();
    if (i !== currentPlayer || !showAim) {
      v.trajectoryGfx.clear();
      if (i === currentPlayer && trajCache) trajCache = null;
    }

    if (!p.alive) continue;

    // Redraw body + flash when tankId or skin changes (e.g. after tank select or skin shop)
    const skin = getSkinForTank(p.tankId);
    const curSkinBodyId = skin.bodyShape ?? p.tankId;
    const curSkinNubId = skin.nubType ?? p.tankId;
    const curSkinColor = skin.color ?? p.color;
    const curSkinBorderId = skin.borderStyle ?? '';
    const curSkinTitle = skin.title ?? '';
    const curSkinTitleRainbow = skin.titleRainbow ?? false;
    const curSkinTitleColor = skin.titleColor ?? curSkinColor;
    const skinChanged = v.drawnTankId !== p.tankId ||
      v.skinBodyId !== curSkinBodyId ||
      v.skinNubId !== curSkinNubId ||
      v.skinColor !== curSkinColor ||
      v.skinBorderId !== curSkinBorderId;
    const titleChanged = v.skinTitle !== curSkinTitle ||
      v.skinTitleColor !== curSkinTitleColor ||
      v.skinTitleRainbow !== curSkinTitleRainbow;

    if (titleChanged) {
      v.skinTitle = curSkinTitle;
      v.skinTitleColor = curSkinTitleColor;
      v.skinTitleRainbow = curSkinTitleRainbow;
      const titleEntry = TITLES.find(t => t.id === curSkinTitle);
      const hasTitle = curSkinTitle !== '' && curSkinTitle !== 'none' && titleEntry;
      v.titleText.text = hasTitle ? titleEntry.label : '';
      v.titleText.visible = !!hasTitle;
      if (hasTitle && !curSkinTitleRainbow) {
        v.titleText.style.fill = curSkinTitleColor;
        (v.titleText.style.dropShadow as { color: number }).color = curSkinTitleColor;
      }
    }

    if (skinChanged) {
      const bodyShapeDef = lookupTank(curSkinBodyId);
      v.tankRadius = bodyShapeDef.visual.bodyRadius;
      v.drawnTankId = p.tankId;
      v.skinBodyId = curSkinBodyId;
      v.skinNubId = curSkinNubId;
      v.skinColor = curSkinColor;
      v.skinBorderId = curSkinBorderId;

      // Redraw body wireframe
      v.bodyGfx.clear();
      drawTankShape(v.bodyGfx, curSkinBodyId, v.tankRadius, curSkinColor);

      // Redraw flash overlay wireframe
      v.flashOverlay.clear();
      strokeTankShape(v.flashOverlay, curSkinBodyId, v.tankRadius, 0xffffff, 3, 1.0);
      const isDuelist = curSkinNubId === 'duelist';
      if (isDuelist) {
        v.flashOverlay.moveTo(SABER_HILT_LENGTH, 0);
        v.flashOverlay.lineTo(SABER_HILT_LENGTH + SABER_LENGTH, 0);
        v.flashOverlay.stroke({ color: 0xffffff, width: 3 });
      } else {
        v.flashOverlay.moveTo(0, 0);
        v.flashOverlay.lineTo(TANK_VISUAL.TURRET_LENGTH, 0);
        v.flashOverlay.stroke({ color: 0xffffff, width: 3 });
        v.flashOverlay.circle(TANK_VISUAL.TURRET_LENGTH, 0, TANK_VISUAL.TIP_RADIUS);
        v.flashOverlay.stroke({ color: 0xffffff, width: 3 });
      }
      v.flashOverlay.visible = false;

      // Clean up old animated tip child
      if (v.tipGfx) {
        v.tipGfx.destroy();
        v.tipGfx = undefined;
      }

      // Redraw turret
      v.barrelGfx.clear();
      if (isDuelist) {
        drawWireframeSaber(v.barrelGfx);
      } else {
        v.tipGfx = drawTankBarrel(v.barrelGfx, curSkinNubId, curSkinColor);
        if (v.tipGfx) v.turretGfx.addChild(v.tipGfx);
      }

    }

    // Position outerContainer at player x,y (lifted so visual bottom aligns with physics feet)
    v.outerContainer.position.set(p.x, p.y - (v.tankRadius - PHYSICS.ECB_RADIUS));

    // Dim frozen tanks during defensive movement
    v.outerContainer.alpha = p.actionState.tag === 'defensive_frozen' ? 0.4 : 1.0;

    // Respawn invulnerability flash — alternating alpha
    if (p.respawnInvulnFrames > 0) {
      const flashPhase = Math.floor(p.respawnInvulnFrames / 6) % 2;
      v.outerContainer.alpha = flashPhase === 0 ? 0.3 : 1.0;
    }

    // Tutorial opponent fade-in after reposition
    if (tutorialOpponentFade < 1 && i === 1) {
      v.outerContainer.alpha *= tutorialOpponentFade;
    }

    // Squash-stretch spring — volume-preserving deformation
    v.outerContainer.scale.set(1 / p.squash, p.squash);

    // Body rotates with movement roll
    v.bodyGfx.rotation = p.rotation;

    // Turret/sword rotation — all tanks follow aim angle
    // p.angle: 0 = right, PI/2 = up. Screen Y inverted, so negate.
    v.turretGfx.rotation = -p.angle;

    if (v.skinNubId === 'duelist') {
      // Idle glow pulse on saber (subtle alpha oscillation)
      const pulseT = Math.sin(Date.now() * 0.003) * 0.15 + 0.85; // 0.7-1.0
      v.turretGfx.alpha = pulseT;
    }

    // Animated barrel tip rotation (Vortex spiral, Gyro propeller)
    if (v.tipGfx) {
      v.tipGfx.rotation += dt * getNubAnimSpeed(v.skinNubId);
    }

    // Flash overlay follows body rotation
    v.flashOverlay.rotation = p.rotation;

    // Border style overlay (follows body rotation, drawn every frame for animation)
    v.borderGfx.rotation = p.rotation;
    drawBorderOverlay(v.borderGfx, v.skinBorderId, v.skinBodyId, v.tankRadius, Date.now() / 1000);

    // Title text — rainbow cycling
    if (v.skinTitleRainbow && v.titleText.visible) {
      const hue = (Date.now() * 0.12) % 360;
      const rainbowColor = hueToHex(hue);
      v.titleText.style.fill = rainbowColor;
      (v.titleText.style.dropShadow as { color: number }).color = rainbowColor;
    }

    // === Sakurai: Character flash on hit ===
    // Rapidly alternate between bright and dark flash colors
    if (v.flashTimer > 0) {
      v.flashTimer -= dt;
      v.flashOverlay.visible = true;

      // Alternate bright/dark every ~60ms (Sakurai: mix bright and dark)
      const flashPhase = Math.floor(v.flashTimer * 16) % 2; // ~16 cycles/sec
      const isBright = flashPhase === 0;
      v.flashOverlay.tint = isBright ? v.flashColor : v.flashDarkColor;
      // Fade out over duration
      const t = Math.max(0, v.flashTimer / 0.35);
      v.flashOverlay.alpha = isBright ? 0.6 * t : 0.4 * t;

      if (v.flashTimer <= 0) {
        v.flashOverlay.visible = false;
        v.flashTimer = 0;
      }
    }

    // === Ragdoll alpha pulse + spin sparks ===
    // Two sub-states: hitstun (accelerating pulse, full sparks) and recovery (steady ramp, dampened spin)
    if (p.tumble) {
      const inRecovery = p.actionState.tag === 'tumble_recovery';

      if (inRecovery) {
        // Recovery: steady alpha ramp from 0.7 to 1.0
        const ramp = p.tumbleRecoveryMax > 0 ? p.tumbleRecoveryFrame / p.tumbleRecoveryMax : 1;
        v.bodyGfx.tint = 0xffffff;
        v.bodyGfx.alpha = 0.7 + ramp * 0.3;
      } else {
        // Hitstun: accelerating alpha pulse
        const timerFrac = Math.min(1, p.hitstunFrames / TUMBLE.PULSE_TIMER_REF);
        const pulseSpeed = TUMBLE.PULSE_SPEED_BASE + (1 - timerFrac) * TUMBLE.PULSE_SPEED_ACCEL;
        const pulse = 0.5 + Math.sin(Date.now() * pulseSpeed) * 0.5;
        v.bodyGfx.tint = 0xffffff;
        v.bodyGfx.alpha = 0.6 + pulse * 0.4;
      }

      // Spin sparks — tangential particles flying off the spinning body
      // Recovery: slower cooldown, dampened spin
      if (particles) {
        const spinRate = Math.abs(p.vx * TUMBLE.SPIN_RATE + p.vy * TUMBLE.SPIN_VY_RATE);
        v.spinSparkCooldown--;

        const minCooldown = inRecovery ? TUMBLE.RECOVERY_SPARK_COOLDOWN : TUMBLE.SPIN_SPARK_COOLDOWN_FAST;
        const maxCooldown = inRecovery ? TUMBLE.RECOVERY_SPARK_COOLDOWN : TUMBLE.SPIN_SPARK_COOLDOWN_BASE;

        if (spinRate >= TUMBLE.SPIN_SPARK_MIN_RATE && v.spinSparkCooldown <= 0) {
          // Normalize spin rate for interpolation (0 at min, 1 at ~0.5 rad/frame)
          const spinT = Math.min(1, (spinRate - TUMBLE.SPIN_SPARK_MIN_RATE) / 0.4);

          // Cooldown: faster spin = more frequent sparks
          v.spinSparkCooldown = Math.round(maxCooldown - spinT * (maxCooldown - minCooldown));

          // Spawn position: random point on body perimeter (visual center is offset up)
          const perimAngle = p.rotation + Math.random() * Math.PI * 2;
          const visualY = p.y - (v.tankRadius - PHYSICS.ECB_RADIUS);
          const spawnX = p.x + Math.cos(perimAngle) * v.tankRadius;
          const spawnY = visualY + Math.sin(perimAngle) * v.tankRadius;

          // Tangential direction (perpendicular to radius, in spin direction)
          const spinSign = (p.vx * TUMBLE.SPIN_RATE + p.vy * TUMBLE.SPIN_VY_RATE) >= 0 ? 1 : -1;
          const tangentAngle = perimAngle + spinSign * Math.PI / 2;
          const sparkSpeed = TUMBLE.SPIN_SPARK_SPEED_MIN + spinT * (TUMBLE.SPIN_SPARK_SPEED_MAX - TUMBLE.SPIN_SPARK_SPEED_MIN);

          // Layer 1: Dark framing (Sakurai contrast)
          particles.spawnParticleAtAngle(
            spawnX, spawnY,
            tangentAngle + (Math.random() - 0.5) * 0.4, sparkSpeed * 0.7,
            0x050520,
            TUMBLE.SPIN_SPARK_DARK_LIFE, TUMBLE.SPIN_SPARK_DARK_SCALE,
            0.04, 0.96,
            false, true, // NORMAL blend, hard texture
            0.5,
          );

          // Layer 2: Bright spark (player color, stretched along travel)
          const sparkCount = spinT > 0.5 ? 2 : 1;
          for (let s = 0; s < sparkCount; s++) {
            particles.spawnParticleAtAngle(
              spawnX, spawnY,
              tangentAngle + (Math.random() - 0.5) * 0.6, sparkSpeed + Math.random() * 1.5,
              p.color,
              TUMBLE.SPIN_SPARK_LIFE, TUMBLE.SPIN_SPARK_SCALE,
              0.04, 0.97,
              true, true, // ADD blend, hard texture
              0.8,
              2.5, // stretched along travel direction
            );
          }

          // Layer 3: White hot pop (brief flash at spawn point)
          particles.spawnParticleAtAngle(
            spawnX, spawnY,
            tangentAngle, sparkSpeed * 0.3,
            0xffffff,
            0.06, 0.6,
            0, 0.95,
            true, false, // ADD blend, soft texture
            0.5,
          );
        }
      }
    }

    // === Armed pulse — red-orange glow while shot is armed ===
    else if (p.armedShotTier !== null) {
      const pulse = 0.5 + Math.sin(Date.now() * 0.006) * 0.5;
      v.bodyGfx.tint = 0xff4400;
      v.bodyGfx.alpha = 0.75 + pulse * 0.25;
    }

    // === Lift alpha pulse — amber tint during active lift ===
    else if (p.liftActive) {
      const pulse = 0.5 + Math.sin(Date.now() * 0.008) * 0.5;
      v.bodyGfx.tint = 0xffbb00;
      v.bodyGfx.alpha = 0.85 + pulse * 0.15;
    }

    // === Damage % urgency alpha pulse ===
    // Increasing pulse intensity communicates danger without polluting wireframe colors
    else {
      v.bodyGfx.tint = 0xffffff;
      const dmg = p.damagePercent;

      if (dmg >= 150) {
        // Critical — fast pulse, deep alpha dip
        const pulse = 0.5 + Math.sin(Date.now() * 0.012) * 0.5;
        v.bodyGfx.alpha = 0.5 + pulse * 0.5;
      } else if (dmg >= 100) {
        // High — medium pulse
        const pulse = 0.5 + Math.sin(Date.now() * 0.008) * 0.5;
        v.bodyGfx.alpha = 0.7 + pulse * 0.3;
      } else if (dmg >= 50) {
        // Medium — slow subtle pulse
        const pulse = 0.5 + Math.sin(Date.now() * 0.004) * 0.5;
        v.bodyGfx.alpha = 0.85 + pulse * 0.15;
      } else {
        // Low damage — full brightness
        v.bodyGfx.alpha = 1.0;
      }
    } // end else (not ragdoll)

    // === Cupid mark visual — small pulsing pink heart above marked tank ===
    if (p.cupidMarkTurns > 0) {
      const markAlpha = 0.5 + Math.sin(Date.now() * 0.006) * 0.3;
      const heartY = p.y - (v.tankRadius - PHYSICS.ECB_RADIUS) - v.tankRadius - 18;
      const heartX = p.x;
      const hs = 6; // heart half-size

      // Draw tiny heart using lines on aimLine (world-space, cleared each frame)
      v.aimLine.moveTo(heartX, heartY + hs);
      v.aimLine.bezierCurveTo(heartX - hs * 0.5, heartY + hs * 0.3, heartX - hs, heartY - hs * 0.3, heartX - hs * 0.5, heartY - hs * 0.3);
      v.aimLine.bezierCurveTo(heartX - hs * 0.1, heartY - hs * 0.3, heartX, heartY - hs * 0.1, heartX, heartY - hs * 0.15);
      v.aimLine.bezierCurveTo(heartX, heartY - hs * 0.1, heartX + hs * 0.1, heartY - hs * 0.3, heartX + hs * 0.5, heartY - hs * 0.3);
      v.aimLine.bezierCurveTo(heartX + hs, heartY - hs * 0.3, heartX + hs * 0.5, heartY + hs * 0.3, heartX, heartY + hs);
      v.aimLine.fill({ color: 0xff5588, alpha: markAlpha });
      v.aimLine.stroke({ color: 0xff88aa, width: 1.5, alpha: markAlpha });
    }

    // === Inactive tank dimming — turn indication lives on the tanks, not HUD cards ===
    if (i !== currentPlayer) {
      v.bodyGfx.alpha *= 0.4;
    }

    // === Momentum energy cape (flows opposite to aim direction) ===
    // Primary: cape flows opposite to turret aim — gives the tank a facing direction.
    // Secondary: movement velocity adds trailing force on top.
    // Direction changes whip naturally (old particles keep momentum).
    // Energy cape disabled — pending rework
    if (false as boolean && i === currentPlayer && particles && p.momentum > 0) {
      const mFrac = Math.min(1, p.momentum / MOMENTUM.MAX);

      // Aim-based "back" direction (opposite to turret aim, in screen coords)
      // p.angle: 0=right, PI/2=up. Back = opposite direction.
      const aimBackNx = -Math.cos(p.angle);          // unit back vector X
      const aimBackNy = Math.sin(p.angle);            // unit back vector Y (+Y = screen down)

      // Movement detection — low threshold so ground walking triggers flow
      const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      const moveT = Math.min(1, spd / 1.2); // 0 = still, 1 = moderate walk speed

      // Idle: drape downward with gentle sway (gravity-dominated, minimal aim influence)
      const swayX = Math.sin(Date.now() * 0.003) * MOMENTUM.AURA_DRAPE_SWAY;
      const idleVx = swayX + aimBackNx * 0.3; // very subtle aim bias at idle
      const idleVy = MOMENTUM.AURA_DRAPE_SPEED;    // drape downward

      // Moving: aim-back direction + movement trail
      const aimBackVx = aimBackNx * MOMENTUM.AURA_DRAPE_SPEED;
      const aimBackVy = aimBackNy * MOMENTUM.AURA_DRAPE_SPEED;
      const trailVx = spd > 0.3 ? -p.vx * MOMENTUM.AURA_TRAIL_INHERIT : 0;
      const trailVy = spd > 0.3 ? -p.vy * MOMENTUM.AURA_TRAIL_INHERIT : 0;
      const moveVx = aimBackVx + trailVx;
      const moveVy = aimBackVy + trailVy;

      // Blend: idle (drape down) → moving (aim-back + trail)
      const baseVx = idleVx * (1 - moveT) + moveVx * moveT;
      const baseVy = idleVy * (1 - moveT) + moveVy * moveT;

      // Spawn position: offset behind body
      const spawnX = p.x + aimBackNx * 18;
      const spawnY = p.y + aimBackNy * 18;

      // Perpendicular to aim-back for spread (fans along body's back edge)
      const perpX = -aimBackNy;
      const perpY = aimBackNx;
      // Neck clamp: no particle spawns above this Y (body center + 3px = shoulder height)
      const neckY = p.y + 3;

      // Stagger timing: alternate layers across frames (Sakurai: "stagger timing")
      // Frame A (even): dark backing + cape flow
      // Frame B (odd): crisp flecks + edge sparks
      auraFrame++;
      const layerA = (auraFrame & 1) === 0; // dark backing + flow
      const layerB = !layerA;               // crisp flecks + sparks

      // Layer 1: Dark backing — Sakurai "mix in darker elements" (spawns on A frames)
      if (layerA) {
        const darkCount = Math.round(MOMENTUM.AURA_DARK_COUNT_MIN + mFrac * (MOMENTUM.AURA_DARK_COUNT_MAX - MOMENTUM.AURA_DARK_COUNT_MIN));
        for (let c = 0; c < darkCount; c++) {
          const s = (Math.random() - 0.5) * MOMENTUM.AURA_FLOW_SPREAD * 1.6;
          const dScale = MOMENTUM.AURA_DARK_SCALE_MIN + Math.random() * (MOMENTUM.AURA_DARK_SCALE_MAX - MOMENTUM.AURA_DARK_SCALE_MIN);
          const dLife = MOMENTUM.AURA_DARK_LIFE_MIN + Math.random() * (MOMENTUM.AURA_DARK_LIFE_MAX - MOMENTUM.AURA_DARK_LIFE_MIN);
          const jx = baseVx + (Math.random() - 0.5) * 0.4;
          const jy = baseVy + (Math.random() - 0.5) * 0.3;
          const angle = Math.atan2(jy, jx);
          const vel = Math.sqrt(jx * jx + jy * jy) * 0.8;
          particles.spawnParticleAtAngle(
            spawnX + perpX * s, Math.max(neckY, spawnY + perpY * s) + Math.random() * 3,
            angle, vel,
            MOMENTUM.AURA_DARK_COLOR,
            dLife, dScale,
            MOMENTUM.AURA_GRAVITY, MOMENTUM.AURA_FRICTION,
            false, false,     // normal blend, soft texture — dark depth layer
            MOMENTUM.AURA_DARK_ALPHA,
            2.0 + Math.random() * 1.0,  // stretched, slightly shorter than flow
          );
        }
      }

      // Layer 2: Cape flow — elongated wisps forming the flowing body (spawns on A frames)
      if (layerA) {
        const flowCount = Math.round(MOMENTUM.AURA_FLOW_COUNT_MIN + mFrac * (MOMENTUM.AURA_FLOW_COUNT_MAX - MOMENTUM.AURA_FLOW_COUNT_MIN));
        for (let c = 0; c < flowCount; c++) {
          const s = (Math.random() - 0.5) * MOMENTUM.AURA_FLOW_SPREAD * 2;
          const fAlpha = MOMENTUM.AURA_FLOW_ALPHA_MIN + mFrac * (MOMENTUM.AURA_FLOW_ALPHA_MAX - MOMENTUM.AURA_FLOW_ALPHA_MIN);
          const fScale = MOMENTUM.AURA_FLOW_SCALE_MIN + Math.random() * (MOMENTUM.AURA_FLOW_SCALE_MAX - MOMENTUM.AURA_FLOW_SCALE_MIN);
          const fLife = MOMENTUM.AURA_FLOW_LIFE_MIN + Math.random() * (MOMENTUM.AURA_FLOW_LIFE_MAX - MOMENTUM.AURA_FLOW_LIFE_MIN);
          const jx = baseVx + (Math.random() - 0.5) * 0.6;
          const jy = baseVy + (Math.random() - 0.5) * 0.4;
          const angle = Math.atan2(jy, jx);
          const vel = Math.sqrt(jx * jx + jy * jy);
          particles.spawnParticleAtAngle(
            spawnX + perpX * s, Math.max(neckY, spawnY + perpY * s) + Math.random() * 4,
            angle, vel,
            MOMENTUM.AURA_FLOW_COLOR,
            fLife, fScale,
            MOMENTUM.AURA_GRAVITY, MOMENTUM.AURA_FRICTION,
            false, false,     // normal blend, soft texture — smooth elongated wisps
            fAlpha * (0.6 + Math.random() * 0.4),
            2.0 + Math.random() * 1.5,  // stretch 2-3.5x along travel direction
          );
        }
      }

      // Layer 3: Crisp flecks — hard-edged sparks for contrast (spawns on B frames)
      // Sakurai: "combine crisp effects with soft glow" — these are sharp against the soft flow
      if (layerB) {
        const crispCount = Math.round(MOMENTUM.AURA_CRISP_COUNT_MIN + mFrac * (MOMENTUM.AURA_CRISP_COUNT_MAX - MOMENTUM.AURA_CRISP_COUNT_MIN));
        for (let c = 0; c < crispCount; c++) {
          const s = (Math.random() - 0.5) * MOMENTUM.AURA_FLOW_SPREAD * 1.8;
          const cScale = MOMENTUM.AURA_CRISP_SCALE_MIN + Math.random() * (MOMENTUM.AURA_CRISP_SCALE_MAX - MOMENTUM.AURA_CRISP_SCALE_MIN);
          const cLife = MOMENTUM.AURA_CRISP_LIFE_MIN + Math.random() * (MOMENTUM.AURA_CRISP_LIFE_MAX - MOMENTUM.AURA_CRISP_LIFE_MIN);
          const jx = baseVx + (Math.random() - 0.5) * 0.8;
          const jy = baseVy + (Math.random() - 0.5) * 0.6;
          const angle = Math.atan2(jy, jx);
          const vel = Math.sqrt(jx * jx + jy * jy) * 1.1;
          particles.spawnParticleAtAngle(
            spawnX + perpX * s, Math.max(neckY, spawnY + perpY * s) + Math.random() * 3,
            angle, vel,
            MOMENTUM.AURA_CRISP_COLOR,
            cLife, cScale,
            MOMENTUM.AURA_GRAVITY, 0.96,
            false, true,      // normal blend, HARD texture — crisp defined edges
            MOMENTUM.AURA_CRISP_ALPHA * mFrac,
          );
        }
      }

      // Layer 4: Edge sparks — tiny bright ADD pops along the cape (spawns on B frames)
      if (layerB) {
        const sparkCount = Math.round(MOMENTUM.AURA_SPARK_COUNT_MIN + mFrac * (MOMENTUM.AURA_SPARK_COUNT_MAX - MOMENTUM.AURA_SPARK_COUNT_MIN));
        for (let c = 0; c < sparkCount; c++) {
          const s = (Math.random() - 0.5) * MOMENTUM.AURA_FLOW_SPREAD * 1.5;
          const sLife = MOMENTUM.AURA_SPARK_LIFE_MIN + Math.random() * (MOMENTUM.AURA_SPARK_LIFE_MAX - MOMENTUM.AURA_SPARK_LIFE_MIN);
          const sScale = MOMENTUM.AURA_SPARK_SCALE_MIN + Math.random() * (MOMENTUM.AURA_SPARK_SCALE_MAX - MOMENTUM.AURA_SPARK_SCALE_MIN);
          const jx = baseVx + (Math.random() - 0.5) * 0.8;
          const jy = baseVy + (Math.random() - 0.5) * 0.6;
          const angle = Math.atan2(jy, jx);
          const vel = Math.sqrt(jx * jx + jy * jy) * 1.2;
          particles.spawnParticleAtAngle(
            spawnX + perpX * s, Math.max(neckY, spawnY + perpY * s) + Math.random() * 3,
            angle, vel,
            MOMENTUM.AURA_SPARK_COLOR,
            sLife, sScale,
            MOMENTUM.AURA_GRAVITY, 0.98,
            true, false,      // ADD blend, soft texture — tiny bright pops
            MOMENTUM.AURA_SPARK_ALPHA * mFrac,
          );
        }
      }
    }

    // === Aim sweep trail + sparks (Sakurai blade trail) ===
    // A trailing angle lags behind the turret. The arc from trailing→turret is drawn
    // as gradient slices: transparent at trailing edge, bright at leading edge.
    // Sakurai blade trail: "fan-shaped polygon, fading over several frames"
    v.sweepTrailGfx.clear();

    const isAiming = showAim && i === currentPlayer;
    const currentScreenAngle = -p.angle; // turret angle in screen coords (Y-inverted)
    const angVel = p.angle - p.aimPrevAngle;
    const absAngVel = Math.abs(angVel);

    // Chase the turret — normal speed while sweeping, fast catch-up when idle (trail dissolves)
    const isMoving = absAngVel > AIM_FX.TRAIL_MIN_DELTA;
    const speed = isMoving ? AIM_FX.TRAIL_CHASE_SPEED : AIM_FX.TRAIL_FADE_SPEED;
    const lerp = 1 - Math.exp(-speed * dt);
    v.trailAngle += (currentScreenAngle - v.trailAngle) * lerp;

    if (isAiming) {
      const trailSpan = Math.abs(v.trailAngle - currentScreenAngle);

      // Speed factor: 0 at TRAIL_MIN_DELTA, 1 at AIM.MAX_SPEED (normalized sweep intensity)
      const speedRange = AIM.MAX_SPEED - AIM_FX.TRAIL_MIN_DELTA;
      const speedT = speedRange > 0 ? Math.min(1, Math.max(0, (absAngVel - AIM_FX.TRAIL_MIN_DELTA) / speedRange)) : 0;
      const effectiveSpeedAlpha = AIM_FX.TRAIL_SPEED_ALPHA_MIN + speedT * (AIM_FX.TRAIL_SPEED_ALPHA_MAX - AIM_FX.TRAIL_SPEED_ALPHA_MIN);

      if (trailSpan > 0.03) { // minimum visible arc
        const innerR = AIM_FX.TRAIL_INNER_RADIUS;
        // Fix 3: outer radius grows with speed — fast sweeps produce wider, more aggressive trails
        const outerR = AIM_FX.TRAIL_OUTER_RADIUS + speedT * AIM_FX.TRAIL_SPEED_RADIUS_BONUS;
        const sliceCount = AIM_FX.TRAIL_SLICES;
        const stepsPerSlice = Math.max(3, Math.ceil(trailSpan * 8));

        // Helper: build arc polygon for one slice
        const buildSlice = (aFrom: number, aTo: number, rIn: number, rOut: number): number[] => {
          const pts: number[] = [];
          for (let s = 0; s <= stepsPerSlice; s++) {
            const t = s / stepsPerSlice;
            const a = aFrom + (aTo - aFrom) * t;
            pts.push(Math.cos(a) * rOut, Math.sin(a) * rOut);
          }
          for (let s = stepsPerSlice; s >= 0; s--) {
            const t = s / stepsPerSlice;
            const a = aFrom + (aTo - aFrom) * t;
            pts.push(Math.cos(a) * rIn, Math.sin(a) * rIn);
          }
          return pts;
        };

        // Draw gradient slices from trailing edge to leading edge
        for (let si = 0; si < sliceCount; si++) {
          const t0 = si / sliceCount;
          const t1 = (si + 1) / sliceCount;
          const a0 = v.trailAngle + (currentScreenAngle - v.trailAngle) * t0;
          const a1 = v.trailAngle + (currentScreenAngle - v.trailAngle) * t1;

          // Alpha ramps from 0 at trailing edge to full at leading edge (quadratic for sharper tip)
          const sliceAlpha = t1 * t1; // 0→1 quadratic — most visible near turret

          // Dark backing (slightly wider — Sakurai contrast framing)
          const darkPts = buildSlice(a0, a1, innerR - 1, outerR + 1);
          v.sweepTrailGfx.poly(darkPts);
          v.sweepTrailGfx.fill({ color: AIM_FX.TRAIL_DARK_COLOR, alpha: AIM_FX.TRAIL_DARK_ALPHA * sliceAlpha * effectiveSpeedAlpha });

          // Bright player-color layer
          const brightPts = buildSlice(a0, a1, innerR, outerR);
          v.sweepTrailGfx.poly(brightPts);
          v.sweepTrailGfx.fill({ color: p.color, alpha: AIM_FX.TRAIL_BRIGHT_ALPHA * sliceAlpha * effectiveSpeedAlpha });

          // Fix 1: White highlight on leading slices (Sakurai layered sharpness: dark → bright → white)
          // Only on the last 2 slices (near turret) — narrower than bright layer
          if (si >= sliceCount - 2) {
            const hlInset = AIM_FX.TRAIL_HIGHLIGHT_INSET;
            const hlPts = buildSlice(a0, a1, innerR + hlInset, outerR - hlInset);
            v.sweepTrailGfx.poly(hlPts);
            v.sweepTrailGfx.fill({ color: 0xffffff, alpha: AIM_FX.TRAIL_HIGHLIGHT_ALPHA * sliceAlpha * effectiveSpeedAlpha });
          }
        }

        // Leading edge stroke — crisp bright accent at turret position (Sakurai blade edge)
        const edgeR = outerR + 2;
        v.sweepTrailGfx.moveTo(
          Math.cos(currentScreenAngle) * innerR,
          Math.sin(currentScreenAngle) * innerR,
        );
        v.sweepTrailGfx.lineTo(
          Math.cos(currentScreenAngle) * edgeR,
          Math.sin(currentScreenAngle) * edgeR,
        );
        v.sweepTrailGfx.stroke({
          width: AIM_FX.TRAIL_EDGE_WIDTH,
          color: 0xffffff,
          alpha: AIM_FX.TRAIL_EDGE_ALPHA * effectiveSpeedAlpha,
        });
      }

      // Fix 2: Spark intensity scales with speed — faster sweeps spawn more/larger sparks
      if (v.aimSparkCooldown > 0) v.aimSparkCooldown--;
      if (absAngVel > AIM_FX.SPARK_THRESHOLD && v.aimSparkCooldown <= 0 && particles) {
        const sparkVisualY = p.y - (v.tankRadius - PHYSICS.ECB_RADIUS);
        const tipX = p.x + Math.cos(-p.angle) * (TANK_VISUAL.TURRET_LENGTH + TANK_VISUAL.TIP_RADIUS);
        const tipY = sparkVisualY + Math.sin(-p.angle) * (TANK_VISUAL.TURRET_LENGTH + TANK_VISUAL.TIP_RADIUS);
        const tangent = -p.angle + (angVel > 0 ? -Math.PI / 2 : Math.PI / 2);
        // Spark speed factor: 0 at threshold, 1 at MAX_SPEED
        const sparkRange = AIM.MAX_SPEED - AIM_FX.SPARK_THRESHOLD;
        const sparkT = sparkRange > 0 ? Math.min(1, (absAngVel - AIM_FX.SPARK_THRESHOLD) / sparkRange) : 0;
        const sparkCount = Math.round(AIM_FX.SPARK_COUNT_MIN + sparkT * (AIM_FX.SPARK_COUNT_MAX - AIM_FX.SPARK_COUNT_MIN));
        const sparkScale = AIM_FX.SPARK_SCALE_MIN + sparkT * (AIM_FX.SPARK_SCALE_MAX - AIM_FX.SPARK_SCALE_MIN);
        particles.spawnAimSweepParticles(tipX, tipY, tangent, p.color, sparkCount, sparkScale);
        const cooldown = Math.round(AIM_FX.SPARK_COOLDOWN_MAX - sparkT * (AIM_FX.SPARK_COOLDOWN_MAX - AIM_FX.SPARK_COOLDOWN_MIN));
        v.aimSparkCooldown = cooldown;
      }
    }

    // Aim line — extends from turret tip outward, only during aiming for current player
    if (showAim && i === currentPlayer) {
      const effectivePower = p.power * PHYSICS.MAX_POWER;
      const lineLen = VISUAL.AIM_LINE_BASE + effectivePower * VISUAL.AIM_LINE_POWER_MULT;

      // Start from turret tip position (in world coords)
      const tipX = p.x + Math.cos(-p.angle) * TANK_VISUAL.TURRET_LENGTH;
      const tipY = p.y + Math.sin(-p.angle) * TANK_VISUAL.TURRET_LENGTH;
      const endX = tipX + Math.cos(-p.angle) * lineLen;
      const endY = tipY + Math.sin(-p.angle) * lineLen;

      v.aimLine.moveTo(tipX, tipY);
      v.aimLine.lineTo(endX, endY);
      v.aimLine.stroke({ width: 2, color: p.color, alpha: 0.5 });

      // Trajectory preview — routing by weapon tracerMode
      const weapon = p.selectedWeapon ?? getDefaultWeapon();
      const mode = weapon.tracerMode ?? 'arc';

      // Non-arc modes that show without charge (sweep = instantFire, radial = area indicator, homing = target line)
      const alwaysVisible = mode === 'sweep' || mode === 'radial';

      if (p.power <= 0 && !alwaysVisible) {
        if (trajCache) {
          v.trajectoryGfx.clear();
          trajCache = null;
        }
        continue;
      }

      // Quantize inputs to sub-visual precision
      const qAngle = Math.round(p.angle / 0.005) * 0.005;
      const qPower = Math.round(p.power / 0.02) * 0.02;
      const qX = Math.round(p.x);
      const qY = Math.round(p.y);

      // Check cache — skip simulation + Graphics rebuild if inputs match
      // Homing/radial modes always redraw (target position changes, pulse animates)
      if (
        mode !== 'homing' &&
        trajCache &&
        trajCache.angle === qAngle &&
        trajCache.power === qPower &&
        trajCache.x === qX &&
        trajCache.y === qY &&
        trajCache.wind === wind &&
        trajCache.playerIndex === i &&
        trajCache.color === p.color &&
        trajCache.weaponId === weapon.id
      ) {
        continue; // Graphics already has the right dots
      }

      v.trajectoryGfx.clear();

      // ── Route by tracer mode ──
      if (mode === 'hitscan') {
        // Straight line from turret tip along aim angle until terrain or world edge
        const rayAngle = -p.angle;
        const stepSize = 4;
        let rx = tipX, ry = tipY;
        let hitX = rx, hitY = ry;
        for (let s = 0; s < 600; s++) {
          rx += Math.cos(rayAngle) * stepSize;
          ry += Math.sin(rayAngle) * stepSize;
          if (rx < WORLD.WORLD_LEFT || rx > WORLD.WORLD_RIGHT || ry < WORLD.WORLD_TOP || ry > WORLD.VIRTUAL_HEIGHT + 100) {
            hitX = rx; hitY = ry;
            break;
          }
          if (isPointInTerrain(rx, ry, true)) {
            hitX = rx; hitY = ry;
            break;
          }
          hitX = rx; hitY = ry;
        }
        // Solid thin line with alpha fade at the end
        const dx = hitX - tipX, dy = hitY - tipY;
        const len = Math.sqrt(dx * dx + dy * dy);
        const segments = 20;
        for (let s = 0; s < segments; s++) {
          const t0 = s / segments, t1 = (s + 1) / segments;
          const alpha = 0.5 * (1 - t0 * 0.6); // fade from 0.5 to 0.2
          v.trajectoryGfx.moveTo(tipX + dx * t0, tipY + dy * t0);
          v.trajectoryGfx.lineTo(tipX + dx * t1, tipY + dy * t1);
          v.trajectoryGfx.stroke({ width: 2, color: weapon.color, alpha });
        }
        // Glow pass
        v.trajectoryGfx.moveTo(tipX, tipY);
        v.trajectoryGfx.lineTo(hitX, hitY);
        v.trajectoryGfx.stroke({ width: 6, color: weapon.color, alpha: 0.08 });

      } else if (mode === 'dash') {
        // Directional line from tank showing dash range, length depends on charge tier
        const charge = p.power; // linear charge 0..1
        let dashRange: number;
        if (charge >= DUELIST_SLASH.LONG_THRESHOLD) {
          dashRange = DUELIST_SLASH.LONG_RANGE;
        } else if (charge >= DUELIST_SLASH.MID_THRESHOLD) {
          dashRange = DUELIST_SLASH.MID_RANGE;
        } else {
          dashRange = DUELIST_SLASH.SHORT_RANGE;
        }
        const dashAngle = -p.angle; // screen-space
        const dashEndX = p.x + Math.cos(dashAngle) * dashRange;
        const dashEndY = p.y + Math.sin(dashAngle) * dashRange;

        // Main dash line
        v.trajectoryGfx.moveTo(p.x, p.y);
        v.trajectoryGfx.lineTo(dashEndX, dashEndY);
        v.trajectoryGfx.stroke({ width: 2.5, color: 0xff2266, alpha: 0.4 });

        // Glow
        v.trajectoryGfx.moveTo(p.x, p.y);
        v.trajectoryGfx.lineTo(dashEndX, dashEndY);
        v.trajectoryGfx.stroke({ width: 7, color: 0xff2266, alpha: 0.08 });

        // Tier threshold tick marks
        const tiers = [DUELIST_SLASH.SHORT_RANGE, DUELIST_SLASH.MID_RANGE, DUELIST_SLASH.LONG_RANGE];
        const perpX = -Math.sin(dashAngle) * 8;
        const perpY = Math.cos(dashAngle) * 8;
        for (const tierDist of tiers) {
          if (tierDist > dashRange) break;
          const tx = p.x + Math.cos(dashAngle) * tierDist;
          const ty = p.y + Math.sin(dashAngle) * tierDist;
          v.trajectoryGfx.moveTo(tx - perpX, ty - perpY);
          v.trajectoryGfx.lineTo(tx + perpX, ty + perpY);
          v.trajectoryGfx.stroke({ width: 1.5, color: 0xff2266, alpha: 0.35 });
        }

        // Endpoint indicator
        v.trajectoryGfx.circle(dashEndX, dashEndY, 4);
        v.trajectoryGfx.fill({ color: 0xff2266, alpha: 0.5 });

      } else if (mode === 'sweep') {
        // Overhead arc sweep showing the tipper blade path
        const arcRadius = 180;
        const facingRight = Math.cos(p.angle) >= 0;
        const dirX = facingRight ? 1 : -1;
        const arcSteps = 24;

        for (let step = 0; step < arcSteps; step++) {
          const t0 = step / arcSteps;
          const t1 = (step + 1) / arcSteps;
          const a0 = -Math.PI / 2 + (Math.PI / 2) * t0;
          const a1 = -Math.PI / 2 + (Math.PI / 2) * t1;
          const x0 = p.x + Math.cos(a0) * arcRadius * dirX;
          const y0 = p.y + Math.sin(a0) * arcRadius;
          const x1 = p.x + Math.cos(a1) * arcRadius * dirX;
          const y1 = p.y + Math.sin(a1) * arcRadius;

          // Zone coloring: early (dim), late (medium), tipper (bright crimson)
          let alpha: number;
          let width: number;
          if (t0 >= 0.85) {
            alpha = 0.5;
            width = 3;
          } else if (t0 >= 0.6) {
            alpha = 0.3;
            width = 2;
          } else {
            alpha = 0.15;
            width = 1.5;
          }

          v.trajectoryGfx.moveTo(x0, y0);
          v.trajectoryGfx.lineTo(x1, y1);
          v.trajectoryGfx.stroke({ width, color: 0xff2266, alpha });
        }

        // Tipper zone glow (last 15% of arc)
        for (let step = Math.floor(arcSteps * 0.85); step < arcSteps; step++) {
          const t0 = step / arcSteps;
          const t1 = (step + 1) / arcSteps;
          const a0 = -Math.PI / 2 + (Math.PI / 2) * t0;
          const a1 = -Math.PI / 2 + (Math.PI / 2) * t1;
          const x0 = p.x + Math.cos(a0) * arcRadius * dirX;
          const y0 = p.y + Math.sin(a0) * arcRadius;
          const x1 = p.x + Math.cos(a1) * arcRadius * dirX;
          const y1 = p.y + Math.sin(a1) * arcRadius;
          v.trajectoryGfx.moveTo(x0, y0);
          v.trajectoryGfx.lineTo(x1, y1);
          v.trajectoryGfx.stroke({ width: 8, color: 0xff0044, alpha: 0.1 });
        }

      } else if (mode === 'cone') {
        // Fanned dotted arcs — config-driven (Scatter pellets, Shooter bullets, etc.)
        const cfg = weapon.tracerConeConfig ?? { subWeaponId: 'scatter_pellet', count: 5, spread: 0.18 };
        const subWeapon = lookupWeapon(cfg.subWeaponId);
        const launchSpeed = chargeToPower(p.power) * PHYSICS.MAX_POWER * weapon.projectileSpeed;
        const spawnOffset = PHYSICS.ECB_RADIUS + weapon.projectileRadius + 2;
        const sx = p.x + Math.cos(p.angle) * spawnOffset;
        const sy = p.y - Math.sin(p.angle) * spawnOffset;
        const baseAngle = Math.atan2(-Math.sin(p.angle) * launchSpeed, Math.cos(p.angle) * launchSpeed);

        for (let pellet = 0; pellet < cfg.count; pellet++) {
          const t = cfg.count > 1 ? pellet / (cfg.count - 1) : 0.5;
          const angle = baseAngle + (t - 0.5) * 2 * cfg.spread;
          const pvx = Math.cos(angle) * launchSpeed;
          const pvy = Math.sin(angle) * launchSpeed;
          const pts = simulateTrajectory(sx, sy, pvx, pvy, subWeapon, { wind, windMultiplier: subWeapon.windMultiplier });

          const fadeStart = pts.length * TRAJECTORY.FADE_START;
          for (let j = 0; j < pts.length; j++) {
            let alpha = j < fadeStart
              ? TRAJECTORY.BASE_ALPHA
              : TRAJECTORY.BASE_ALPHA * (1 - (j - fadeStart) / (pts.length - fadeStart));
            alpha *= 0.35; // reduced per-pellet alpha
            v.trajectoryGfx.circle(pts[j].x, pts[j].y, 2);
            v.trajectoryGfx.fill({ color: subWeapon.color, alpha });
          }
        }

      } else if (mode === 'homing') {
        // Dotted pink line to nearest alive enemy
        let bestDist = Infinity;
        let bestIdx = -1;
        for (let ei = 0; ei < players.length; ei++) {
          if (ei === i) continue;
          const ep = players[ei];
          if (!ep.alive) continue;
          const edx = ep.x - p.x;
          const edy = ep.y - p.y;
          const eDist = Math.sqrt(edx * edx + edy * edy);
          if (eDist < bestDist) {
            bestDist = eDist;
            bestIdx = ei;
          }
        }
        if (bestIdx >= 0) {
          const target = players[bestIdx];
          const tdx = target.x - p.x;
          const tdy = target.y - p.y;
          const totalDist = Math.sqrt(tdx * tdx + tdy * tdy);
          const dotSpacing = 12;
          const dotCount = Math.floor(totalDist / dotSpacing);
          // Pulsing alpha via sin wave (uses performance.now for smooth animation)
          const pulseAlpha = 0.15 + 0.12 * Math.sin(performance.now() * 0.004);
          for (let d = 0; d < dotCount; d++) {
            const t = (d + 0.5) / dotCount;
            const dx = p.x + tdx * t;
            const dy = p.y + tdy * t;
            v.trajectoryGfx.circle(dx, dy, 1.5);
            v.trajectoryGfx.fill({ color: 0xff5588, alpha: pulseAlpha });
          }
          // Small crosshair on target
          const sz = 6;
          v.trajectoryGfx.moveTo(target.x - sz, target.y);
          v.trajectoryGfx.lineTo(target.x + sz, target.y);
          v.trajectoryGfx.stroke({ width: 1.5, color: 0xff5588, alpha: pulseAlpha * 1.5 });
          v.trajectoryGfx.moveTo(target.x, target.y - sz);
          v.trajectoryGfx.lineTo(target.x, target.y + sz);
          v.trajectoryGfx.stroke({ width: 1.5, color: 0xff5588, alpha: pulseAlpha * 1.5 });
        }

      } else if (mode === 'radial') {
        // Two concentric heart-shaped indicators for Cupid's Heartbeat
        const cx = p.x, cy = p.y;
        const heartSteps = 32;

        // Heart parametric: x = 16sin³(t), y = -(13cos-5cos2-2cos3-cos4), normalized to ~1
        const hx = (t: number) => { const s = Math.sin(t); return s * s * s; };
        const hy = (t: number) => -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) / 16;

        // Inner heart (beat 1): scale 100
        for (let s = 0; s < heartSteps; s++) {
          const t0 = (s / heartSteps) * Math.PI * 2;
          const t1 = ((s + 1) / heartSteps) * Math.PI * 2;
          v.trajectoryGfx.moveTo(cx + hx(t0) * 100, cy + hy(t0) * 100);
          v.trajectoryGfx.lineTo(cx + hx(t1) * 100, cy + hy(t1) * 100);
          v.trajectoryGfx.stroke({ width: 1.5, color: 0xff5588, alpha: 0.25 });
        }
        // Inner glow
        for (let s = 0; s < heartSteps; s++) {
          const t0 = (s / heartSteps) * Math.PI * 2;
          const t1 = ((s + 1) / heartSteps) * Math.PI * 2;
          v.trajectoryGfx.moveTo(cx + hx(t0) * 100, cy + hy(t0) * 100);
          v.trajectoryGfx.lineTo(cx + hx(t1) * 100, cy + hy(t1) * 100);
          v.trajectoryGfx.stroke({ width: 6, color: 0xff5588, alpha: 0.06 });
        }

        // Outer heart (beat 2): scale 160
        for (let s = 0; s < heartSteps; s++) {
          const t0 = (s / heartSteps) * Math.PI * 2;
          const t1 = ((s + 1) / heartSteps) * Math.PI * 2;
          v.trajectoryGfx.moveTo(cx + hx(t0) * 160, cy + hy(t0) * 160);
          v.trajectoryGfx.lineTo(cx + hx(t1) * 160, cy + hy(t1) * 160);
          v.trajectoryGfx.stroke({ width: 1.5, color: 0xff3366, alpha: 0.15 });
        }
        // Outer glow
        for (let s = 0; s < heartSteps; s++) {
          const t0 = (s / heartSteps) * Math.PI * 2;
          const t1 = ((s + 1) / heartSteps) * Math.PI * 2;
          v.trajectoryGfx.moveTo(cx + hx(t0) * 160, cy + hy(t0) * 160);
          v.trajectoryGfx.lineTo(cx + hx(t1) * 160, cy + hy(t1) * 160);
          v.trajectoryGfx.stroke({ width: 6, color: 0xff3366, alpha: 0.04 });
        }

      } else if (mode === 'meteor') {
        // Falling Star: simulate tank fall path (speed 8, gravity 0.35, vy clamped >= 0)
        const meteorAngle = -p.angle;
        const meteorSpeed = 8;
        let mx = p.x, my = p.y;
        let mvx = Math.cos(meteorAngle) * meteorSpeed;
        let mvy = Math.max(0, Math.sin(meteorAngle) * meteorSpeed);
        const meteorGrav = 0.35;
        let mDist = 0;

        const mPoints: { x: number; y: number }[] = [];
        for (let s = 0; s < 200; s++) {
          mvy += meteorGrav;
          const prevX = mx, prevY = my;
          mx += mvx;
          my += mvy;
          if (mx < WORLD.WORLD_LEFT || mx > WORLD.WORLD_RIGHT) break;
          if (isPointInTerrain(mx, my, true)) { mPoints.push({ x: mx, y: my }); break; }
          if (my > WORLD.VIRTUAL_HEIGHT + 100) break;
          mDist += Math.sqrt((mx - prevX) ** 2 + (my - prevY) ** 2);
          if (mDist >= TRAJECTORY.DOT_SPACING) {
            mPoints.push({ x: mx, y: my });
            mDist -= TRAJECTORY.DOT_SPACING;
          }
        }

        const mFadeStart = mPoints.length * TRAJECTORY.FADE_START;
        for (let j = 0; j < mPoints.length; j++) {
          const alpha = j < mFadeStart
            ? TRAJECTORY.BASE_ALPHA
            : TRAJECTORY.BASE_ALPHA * (1 - (j - mFadeStart) / (mPoints.length - mFadeStart));
          v.trajectoryGfx.circle(mPoints[j].x, mPoints[j].y, TRAJECTORY.DOT_RADIUS);
          v.trajectoryGfx.fill({ color: 0xffd700, alpha });
        }

      } else {
        // Default: arc tracer (original behavior)
        if (p.power <= 0) {
          trajCache = {
            points: [], angle: qAngle, power: qPower,
            x: qX, y: qY, wind, playerIndex: i, color: p.color,
            weaponId: weapon.id,
          };
          continue;
        }
        const launchSpeed = chargeToPower(p.power) * PHYSICS.MAX_POWER * weapon.projectileSpeed;
        const spawnOffset = PHYSICS.ECB_RADIUS + weapon.projectileRadius + 2;
        const sx = p.x + Math.cos(p.angle) * spawnOffset;
        const sy = p.y - Math.sin(p.angle) * spawnOffset;
        const svx = Math.cos(p.angle) * launchSpeed;
        const svy = -Math.sin(p.angle) * launchSpeed;

        const points = simulateTrajectory(sx, sy, svx, svy, weapon, {
          wind, windMultiplier: weapon.windMultiplier,
          maxFrames: weapon.tracerMaxFrames,
          ignoreTerrain: weapon.tracerIgnoreTerrain,
        });

        const fadeStart = points.length * TRAJECTORY.FADE_START;
        for (let j = 0; j < points.length; j++) {
          // Base distance fade
          let alpha = j < fadeStart
            ? TRAJECTORY.BASE_ALPHA
            : TRAJECTORY.BASE_ALPHA * (1 - (j - fadeStart) / (points.length - fadeStart));
          // Bounce segment dimming — each bounce segment gets progressively dimmer
          const bi = points[j].bounceIndex;
          if (bi > 0) {
            const segmentDim = bi === 1 ? 0.6 : bi === 2 ? 0.35 : 0.2;
            alpha *= segmentDim;
          }
          v.trajectoryGfx.circle(points[j].x, points[j].y, TRAJECTORY.DOT_RADIUS);
          v.trajectoryGfx.fill({ color: p.color, alpha });
        }

        // End marker for truncated trajectories (e.g., Homing Missile lock-on point)
        if (weapon.tracerEndMarker && points.length > 0) {
          const last = points[points.length - 1];
          // Crosshair indicator
          const sz = 8;
          v.trajectoryGfx.moveTo(last.x - sz, last.y);
          v.trajectoryGfx.lineTo(last.x + sz, last.y);
          v.trajectoryGfx.stroke({ width: 1.5, color: p.color, alpha: 0.5 });
          v.trajectoryGfx.moveTo(last.x, last.y - sz);
          v.trajectoryGfx.lineTo(last.x, last.y + sz);
          v.trajectoryGfx.stroke({ width: 1.5, color: p.color, alpha: 0.5 });
          // Small circle
          v.trajectoryGfx.circle(last.x, last.y, 5);
          v.trajectoryGfx.stroke({ width: 1, color: p.color, alpha: 0.4 });
        }
      }

      trajCache = {
        points: [], angle: qAngle, power: qPower,
        x: qX, y: qY, wind, playerIndex: i, color: p.color,
        weaponId: weapon.id,
      };
    }
  }
}
